import { ChevronRight } from "@mui/icons-material";
import {
  Badge,
  Box,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from "@mui/material";
import Dialog from "components/NoScrollDialog";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import IndeterminateIcon from "components/IndeterminateIcon";
import MDButton from "components/MDButton";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useLanguage } from "contexts/language.context";
import {
  DatePicker,
  DateTimePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

export default function FilterTasksDialog({
  open,
  handleClose,
  filters,
  onChange,
}) {
  const { label } = useLanguage();
  const [selected, setSelected] = useState(
    filters.findIndex((filter) => filter.options?.length)
  );
  const [extraOptions, setExtraOptions] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();

  const dependableKeys = filters
    .filter((v) => v.dependsOn)
    .map((v) => v.dependsOn);

  const checked = useMemo(
    () => {
      const checked = {};
      filters.map((filter) => {
        checked[filter.key] = searchParams.get(filter.key)?.split("|") || [];
      });
      return checked;
    },
    filters.map((filter) => searchParams.get(filter.key))
  );

  function check(key, value) {
    const newChecked = [...checked[key], value];
    newChecked.sort();

    if (dependableKeys.includes(key)) updateExtraOptions(key, newChecked);

    value = "" + value;
    if (checked[key].includes(value)) return;
    setSearchParams((prev) => {
      prev.set(key, newChecked.join("|"));
      return prev;
    });
  }

  function uncheck(key, value) {
    const newChecked = checked[key].filter((v) => v != value);
    newChecked.sort();

    if (dependableKeys.includes(key)) updateExtraOptions(key, newChecked);
    value = "" + value;
    if (!checked[key].includes(value)) return;
    setSearchParams((prev) => {
      if (!newChecked.length) {
        prev.delete(key);
        return prev;
      }

      prev.set(key, newChecked.join("|"));
      return prev;
    });
  }

  function checkAll(key) {
    const filter = filters.find((filter) => filter.key == key);
    const newChecked = filter.options?.map((option) => option.id);
    newChecked.sort();

    if (dependableKeys.includes(key)) updateExtraOptions(key, newChecked);
    setSearchParams((prev) => {
      prev.set(key, newChecked.join("|"));
      return prev;
    });
  }

  function uncheckAll(key) {
    if (dependableKeys.includes(key)) updateExtraOptions(key, []);
    setSearchParams((prev) => {
      prev.delete(key);
      return prev;
    });
  }

  function clearAll() {
    dependableKeys.map((key) => updateExtraOptions(key, []));
    filters.forEach((filter) => {
      uncheckAll(filter.key);
    });

    setSearchParams((prev) => {
      prev.delete("from");
      prev.delete("to");
      return prev;
    });
  }

  useEffect(() => {
    onChange(checked);
  }, [checked]);

  async function updateExtraOptions(key, value) {
    const filter = filters.find((filter) => filter.dependsOn == key);

    if (!value.length) {
      uncheckAll(filter.key);
      return setExtraOptions((extraOptions) => ({
        ...extraOptions,
        [filter.key]: [],
      }));
    }

    const options = await filter.getOptions(value);

    const unwanted = checked[filter.key].filter(
      (v) => !options.find((o) => o.id == v)
    );
    for (const no of unwanted) {
      uncheck(filter.key, no);
    }

    setExtraOptions((extraOptions) => ({
      ...extraOptions,
      [filter.key]: options,
    }));
  }

  return (
    <Dialog
      maxWidth="sm"
      fullWidth
      open={open}
      onClose={handleClose}
      PaperProps={{
        component: "form",
        onSubmit: async (event) => {
          event.preventDefault();
        },
      }}
    >
      <DialogTitle>{label("filter-plural")}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid
            item
            xs={5}
            sx={() => ({
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            })}
          >
            <Box></Box>
            {filters
              .filter((v) => v.options?.length || v.getOptions || v.type)
              .map((filter) => {
                const index = filters.findIndex((f) => f.key == filter.key);
                return (
                  <Badge
                    key={"Filter: " + filter.key}
                    badgeContent={
                      filter.type == "date"
                        ? ((searchParams.get("from") || 0) && 1) +
                          ((searchParams.get("to") || 0) && 1)
                        : checked[filter.key].length
                    }
                    variant="standard"
                    color="primary"
                    sx={{ width: "100%", zIndex: 100000 }}
                  >
                    <MDButton
                      color={index == selected ? "info" : "secondary"}
                      variant={index == selected ? "gradient" : "outlined"}
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                      }}
                      onClick={() => {
                        setSelected(index);
                      }}
                    >
                      <Box sx={{ ml: -1 }}>{filter.label}</Box>{" "}
                      <ChevronRight
                        sx={{
                          height: "20px",
                          width: "20px",
                          fontSize: "32px !important",
                          mr: -1,
                        }}
                      />
                    </MDButton>
                  </Badge>
                );
              })}
          </Grid>
          <Grid item xs={7}>
            <Details
              onChange={onChange}
              type={filters[selected]?.type}
              options={
                filters[selected]?.getOptions
                  ? extraOptions?.[filters[selected]?.key]
                  : filters[selected]?.options || []
              }
              selected={selected}
              checked={checked}
              check={check}
              uncheck={uncheck}
              checkAll={checkAll}
              uncheckAll={uncheckAll}
              filters={filters}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <MDButton onClick={handleClose}>{label("close-button")}</MDButton>
        <MDButton
          variant="gradient"
          color="primary"
          type="button"
          onClick={clearAll}
        >
          {label("clear-all-filters-button")}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

function Details({
  options,
  selected,
  checked,
  check,
  uncheck,
  filters,
  checkAll,
  uncheckAll,
  type,
  onChange,
}) {
  const { label } = useLanguage();

  const [searchParams, setSearchParams] = useSearchParams();

  const date = useMemo(() => {
    return {
      from: searchParams.get("from")
        ? dayjs(new Date(searchParams.get("from")))
        : null,
      to: searchParams.get("to")
        ? dayjs(new Date(searchParams.get("to")))
        : null,
    };
  }, [searchParams.get("from"), searchParams.get("to")]);

  function setDate(value) {
    setSearchParams((prev) => {
      if (value.from) {
        const fromDate = new Date(value.from.$d);
        fromDate.setHours(0, 0, 0, 0);
        prev.set("from", fromDate.toISOString());
      } else {
        prev.delete("from");
      }

      if (value.to) {
        const toDate = new Date(value.to.$d);
        toDate.setHours(23, 59, 59, 999);
        prev.set("to", toDate.toISOString());
      } else {
        prev.delete("to");
      }

      return prev;
    });
  }

  useEffect(() => {
    onChange();
  }, [date.from, date.to]);

  return (
    <Card
      variant="outlined"
      sx={{
        pb: 1.5,
        height: "100%",
        height: "400px",
        overflowY: "auto",
      }}
    >
      <Box
        sx={(theme) => ({
          backgroundColor: "white !important",
          pt: 0.5,
          px: 2,
          position: "sticky",
          borderBottom: "1px solid",
          borderColor: theme.palette.divider,
          top: 0,
          zIndex: 2,
        })}
      >
        <Typography fontSize={"medium"} fontWeight={"500"} sx={{ py: 0.5 }}>
          {filters[selected]?.label}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          px: 2,
          height: "100%",
          gap: 1,
          py: 1,
        }}
      >
        {type == "date" ? (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1, pt: 1 }}
            >
              <DatePicker
                label={label("from-date-label")}
                value={date.from}
                onChange={(v) => {
                  setDate({ ...date, from: v });
                }}
                sx={{ width: "100%" }}
                format="DD/MM/YYYY"
              />
              <DatePicker
                label={label("to-date-label")}
                value={date.to}
                onChange={(v) => {
                  setDate({ ...date, to: v });
                }}
                sx={{ width: "100%" }}
                format="DD/MM/YYYY"
              />

              <MDButton
                disabled={!date.from && !date.to}
                variant="gradient"
                color="primary"
                onClick={() => {
                  setDate({ from: null, to: null });
                }}
              >
                {label("clear-date-filter")}
              </MDButton>

              {/* <MDButton color="info" variant="gradient">
                Tomorrow
              </MDButton>
              <MDButton color="info" variant="gradient">
                Next month
              </MDButton>
              <MDButton color="info" variant="gradient">
                Past due
              </MDButton> */}
            </Box>{" "}
          </LocalizationProvider>
        ) : options?.length ? (
          <Options
            options={options}
            selected={selected}
            checked={checked}
            check={check}
            uncheck={uncheck}
            filters={filters}
          />
        ) : (
          <Typography
            sx={{
              width: "100%",
              height: "100%",
              textAlign: "center",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontSize: "medium",
            }}
            color="secondary"
          >
            {filters[selected]?.getOptions &&
            !checked[filters[selected]?.dependsOn]?.length
              ? `Select one or many <REPLACE_ME> first`.replace(
                  "<REPLACE_ME>",
                  filters
                    .find((v) => v.key == filters[selected]?.dependsOn)
                    .label.toLowerCase()
                )
              : label("none-found") || "None found"}
          </Typography>
        )}
      </Box>
    </Card>
  );
}

function Options({ options, selected, checked, check, uncheck, filters }) {
  return Object.entries(
    options?.reduce((groups, option) => {
      const groupId = option.groupId || "ungrouped";
      if (!groups[groupId]) {
        groups[groupId] = {
          groupLabel: option.groupLabel || null,
          options: [],
        };
      }
      groups[groupId].options?.push(option);
      return groups;
    }, {})
  ).map(([groupId, group]) => (
    <Box key={groupId} sx={{ mb: 1 }}>
      {group.groupLabel && (
        <Box
          sx={{
            position: "sticky",
            top: 45,
            backgroundColor: "white !important",
            pt: 1,
            pb: 0.5,
            zIndex: 1,
          }}
        >
          <Typography fontWeight="bold" fontSize="medium">
            {group.groupLabel} ({group.options?.length})
          </Typography>
        </Box>
      )}
      {group.options?.map((option) => (
        <FormControlLabel
          key={"Filter: " + selected + " - option: " + option.id}
          sx={{
            display: "flex",
            flexDirection: "row",
            my: 0,
            py: 0,
            pl: group.groupLabel ? 2 : 0, // 16px padding to the left
          }}
          label={<Typography fontSize={"15px"}>{option.label}</Typography>}
          control={
            <Checkbox
              checked={checked[filters[selected]?.key].includes("" + option.id)}
              onChange={(e) => {
                if (e.target.checked) {
                  check(filters[selected]?.key, option.id);
                } else {
                  uncheck(filters[selected]?.key, option.id);
                }
              }}
            />
          }
        />
      ))}
    </Box>
  ));
}
